import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TablePagination,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Collapse,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { fetchTemplates } from './template.api';
import { AnyTemplate, TemplateListParams, TemplateSection } from './template.types';
import { useAccessToken } from '@features/user';
import { formatDate } from '../../../utils/utils'; // Assuming you have a date formatter

interface TemplateListTableProps {
  // onEdit: (template: AnyTemplate) => void; // For when form is ready
}

const TemplateListTable: React.FC<TemplateListTableProps> = (/*{ onEdit }*/) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccessToken } = useAccessToken();
  const [templates, setTemplates] = useState<AnyTemplate[]>([]);
  const [totalTemplates, setTotalTemplates] = useState(0);
  const [page, setPage] = useState(0); // 0-indexed for MUI TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [templateTypeFilter, setTemplateTypeFilter] = useState<'all' | 'public' | 'user'>('all');
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const userIdFromParams = searchParams.get('userId');

  const toggleRowExpansion = (templateId: string, templateVersion: number) => {
    const key = `${templateId}-${templateVersion}`;
    setExpandedRows(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isRowExpanded = (templateId: string, templateVersion: number) => {
    const key = `${templateId}-${templateVersion}`;
    return !!expandedRows[key];
  };

  const handleEditModule = (moduleId: string, moduleType: 'public' | 'user') => {
    // Build navigation URL with appropriate parameters
    let moduleUrl = `/admin/modules?moduleId=${moduleId}&moduleType=${moduleType}`;

    // If we have a userId in our current URL, pass it along for user modules
    if (userIdFromParams && moduleType === 'user') {
      moduleUrl += `&userId=${userIdFromParams}`;
    }

    // Navigate to the module page
    navigate(moduleUrl);
  };

  const handleSearch = () => {
    // Update URL search parameters and reload data
    const newParams = new URLSearchParams(searchParams);

    if (searchTerm) {
      newParams.set('search', searchTerm);
    } else {
      newParams.delete('search');
    }

    if (templateTypeFilter !== 'all') {
      newParams.set('filterByType', templateTypeFilter);
    } else {
      newParams.delete('filterByType');
    }

    // Reset to first page when search/filter changes
    setPage(0);
    newParams.set('page', '1');

    setSearchParams(newParams);
  };

  useEffect(() => {
    // Initialize search state from URL params
    const searchFromParams = searchParams.get('search') || '';
    const typeFilterFromParams =
      (searchParams.get('filterByType') as 'public' | 'user' | null) || 'all';

    setSearchTerm(searchFromParams);
    setTemplateTypeFilter(typeFilterFromParams as 'all' | 'public' | 'user');

    const loadTemplates = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          setError('Authentication token not available.');
          setIsLoading(false);
          return;
        }

        const params: TemplateListParams = {
          page: page + 1, // API is 1-indexed
          pageSize: rowsPerPage,
          search: searchParams.get('search') || undefined,
          sortBy: 'modified_on', // Default sort
          sortOrder: 'desc',
        };

        if (userIdFromParams) {
          params.filterByUserId = userIdFromParams;
        }

        const typeFilter = searchParams.get('filterByType');
        if (typeFilter === 'public' || typeFilter === 'user') {
          params.filterByType = typeFilter;
        }

        const response = await fetchTemplates(params, accessToken);
        setTemplates(response.data);
        setTotalTemplates(response.total);
      } catch (err) {
        console.error('Error fetching templates:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      }
      setIsLoading(false);
    };

    loadTemplates();
  }, [getAccessToken, page, rowsPerPage, userIdFromParams, searchParams]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);

    // Update URL search parameters for pagination
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(newPage + 1)); // 1-indexed for URL
    setSearchParams(newParams);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);

    // Update URL search parameters for page size
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageSize', String(newRowsPerPage));
    newParams.set('page', '1'); // Reset to first page
    setSearchParams(newParams);
  };

  if (isLoading && templates.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {userIdFromParams && (
        <Typography variant="h6" sx={{ p: 2 }}>
          Displaying templates for User ID: {userIdFromParams}
        </Typography>
      )}

      {/* Search and filter controls */}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Search Templates"
              variant="outlined"
              fullWidth
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter') handleSearch();
              }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel id="template-type-filter-label">Template Type</InputLabel>
              <Select
                labelId="template-type-filter-label"
                value={templateTypeFilter}
                label="Template Type"
                onChange={e => {
                  setTemplateTypeFilter(e.target.value as 'all' | 'public' | 'user');
                  // Apply filter immediately when changed
                  setTimeout(handleSearch, 0);
                }}
              >
                <MenuItem value="all">All Templates</MenuItem>
                <MenuItem value="public">Public Templates</MenuItem>
                <MenuItem value="user">User Templates</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2} md={3}>
            <Button variant="contained" onClick={handleSearch} fullWidth>
              Apply Filters
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Loading indicator for filter/search updates */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 50 }}></TableCell>
              <TableCell style={{ minWidth: 170 }}>Name</TableCell>
              <TableCell style={{ minWidth: 100 }}>Type</TableCell>
              <TableCell style={{ minWidth: 100 }}>Status</TableCell>
              <TableCell style={{ minWidth: 100 }}>Sections</TableCell>
              <TableCell style={{ minWidth: 120 }}>Categories</TableCell>
              <TableCell style={{ minWidth: 120 }}>Created</TableCell>
              <TableCell style={{ minWidth: 120 }}>Last Modified</TableCell>
              <TableCell style={{ minWidth: 120 }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No templates found.
                </TableCell>
              </TableRow>
            ) : (
              templates.map(template => {
                const isExpanded = isRowExpanded(template.template_id, template.template_version);

                return (
                  <React.Fragment key={template.template_id + template.template_version}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() =>
                            toggleRowExpansion(template.template_id, template.template_version)
                          }
                        >
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{template.template_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={template._templateType === 'public' ? 'Public' : 'User'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Determine template status
                          if ('deactivated' in template && template.deactivated) {
                            return (
                              <Chip
                                label="Deactivated"
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            );
                          } else if (template._templateType === 'public') {
                            return (
                              <Chip
                                label="Public"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            );
                          } else {
                            return (
                              <Chip
                                label="Active"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            );
                          }
                        })()}
                      </TableCell>
                      <TableCell>{template.sections?.length || 0} sections</TableCell>
                      <TableCell>
                        {template._templateType === 'public' &&
                        template.public_categories &&
                        template.public_categories.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {template.public_categories.map((category, idx) => (
                              <Chip key={idx} label={category} size="small" variant="outlined" />
                            ))}
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{formatDate(template.created_on)}</TableCell>
                      <TableCell>{formatDate(template.modified_on)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit Template (Coming Soon)">
                          <span>
                            <IconButton size="small" disabled>
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Expandable section with modules */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1, py: 2 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Sections
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Section Name</TableCell>
                                  <TableCell>Module ID</TableCell>
                                  <TableCell>In Summary</TableCell>
                                  <TableCell align="center">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {template.sections?.map((section: TemplateSection) => (
                                  <TableRow key={section.module_id}>
                                    <TableCell>{section.display_name}</TableCell>
                                    <TableCell>{section.module_id}</TableCell>
                                    <TableCell>
                                      {template.summary_modules?.includes(section.module_id) ? (
                                        <Chip
                                          label="In Summary"
                                          size="small"
                                          color="info"
                                          variant="outlined"
                                        />
                                      ) : (
                                        '—'
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onClick={() =>
                                          handleEditModule(
                                            section.module_id,
                                            // Use template type as a best guess for module type
                                            template._templateType,
                                          )
                                        }
                                        sx={{ m: 0.5 }}
                                      >
                                        Edit Module
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 20, 50, 100]}
        component="div"
        count={totalTemplates}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default TemplateListTable;
